// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {InputEl} from 'common/components/input';
import {ConfirmEl} from 'common/components/confirm';
import {SpinnerEl} from 'common/components/spinner';
import {TooltipEl} from 'common/components/tooltip';
import {
  getFormattedPropertyName,
  getRawPropertyName,
  getRawPropertyValue,
  getScdEventValue,
} from 'common/util/format-property';
import {FormattedPropertyMixin} from 'common/widgets/profile-activity/formatted-property/template';
import {ButtonTheme} from 'common/components/button/constants';
import {Placement} from 'common/components/tooltip/types';
import {THEME} from 'common/components/confirm/types';
import {ModalEl} from 'common/components/modal';
import {Visibility} from 'common/components/base/stackable';
import {isScdPropertyName} from 'common/report/util/properties';

import {HistoricalPropertyChangeLogEl} from '../historical-property-change-log';
import type {ProfileEditableProperty} from './index';

export function template(this: ProfileEditableProperty) {
  const {t} = this.getContext(`i18n`);

  const {editValue, isEditing, isConfirmingDelete, isPropertyChangeLogOpen} = this.state;
  const {
    'match-parts': matchParts,
    property,
    loading,
    'sidebar-mode': sidebarMode,
    'can-write-profile': hasWriteProfilePermission,
    'data-group-id': dataGroupId,
    'distinct-id': distinctId,
  } = this.attrs();

  const isScdProp = isScdPropertyName(getRawPropertyName(property));
  // For now, we don't have a good scheme to edit list properties, so we disable edits in this case
  const propertyEditDisabled = Array.isArray(getRawPropertyValue(property));

  const EditingProperty = () => {
    const controlBarElement = loading ? (
      <SpinnerEl />
    ) : (
      <>
        <ButtonEl
          sel='.new-property-button'
          attrs={{
            theme: ButtonTheme.Tertiary,
          }}
          on={{
            click: this.helpers.handleCancelPropertyEdit,
          }}
        >
          {t(`common.cancel`)}
        </ButtonEl>
        <ButtonEl
          sel='.new-property-button'
          attrs={{
            theme: ButtonTheme.Primary,
          }}
          on={{
            click: this.helpers.handleSavePropertyValue,
          }}
        >
          {t(`common.save`)}
        </ButtonEl>
      </>
    );

    return (
      <div sel='.editing-property'>
        <InputEl
          sel='.value-input'
          attrs={{
            label: getFormattedPropertyName(property),
          }}
          props={{
            value: editValue,
          }}
          on={{
            input: this.helpers.handleUpdatePropertyValue,
          }}
          hook={{insert: this.helpers.inputInserted}}
        />
        <div sel='.confirmation-buttons'>{controlBarElement}</div>
      </div>
    );
  };

  const PropertyControls = () => {
    if (loading) {
      return (
        <div sel='.loading-spinner-wrapper'>
          <SpinnerEl />
        </div>
      );
    }

    if (isScdProp) {
      return (
        <div sel='.hover-button-wrapper' class={{'is-sidebar': sidebarMode}}>
          <ButtonEl
            sel='.control-button'
            attrs={{
              theme: ButtonTheme.Secondary,
              icon: `time`,
            }}
            on={{click: () => this.helpers.handleOpenPropertyChangelog(true)}}
          >
            View Changes
          </ButtonEl>
        </div>
      );
    }

    if (sidebarMode) {
      return null;
    }

    return (
      <div sel='.hover-button-wrapper'>
        {!hasWriteProfilePermission && (
          <TooltipEl attrs={{placement: Placement.Left}}>You don't have permission to edit.</TooltipEl>
        )}
        {hasWriteProfilePermission && propertyEditDisabled && (
          <TooltipEl attrs={{placement: Placement.Left}}>Editing list properties is disabled.</TooltipEl>
        )}
        <ButtonEl
          attrs={{
            disabled: propertyEditDisabled || !hasWriteProfilePermission,
            theme: ButtonTheme.Tertiary,
            icon: `edit`,
            tabindex: 1,
          }}
          on={{
            click: this.helpers.handleEditProperty,
          }}
        />
        <ButtonEl
          attrs={{
            disabled: !hasWriteProfilePermission,
            theme: ButtonTheme.Tertiary,
            icon: `trashcan`,
            tabindex: 2,
          }}
          on={{
            click: this.helpers.handleDeleteButton,
          }}
        />
      </div>
    );
  };

  const ViewProperty = () => (
    <>
      <div sel='.property-wrapper'>
        <FormattedPropertyMixin
          layout={
            sidebarMode
              ? this.helpers.FormattedPropertyLayout.Horizontal
              : this.helpers.FormattedPropertyLayout.Vertical
          }
          property={property}
          matchParts={matchParts}
        />
      </div>
      <PropertyControls />
    </>
  );

  const ConfirmDelete = () => (
    <ConfirmEl
      attrs={{
        theme: THEME.DELETE,
        'confirm-button-label': `Delete Property`,
        open: true,
      }}
      on={{
        submit: this.helpers.handleModalButton,
      }}
    >
      <div>
        <div sel='.mp-confirm-title'>Delete '{getFormattedPropertyName(property)}' From Profile?</div>
        <div sel='.mp-confirm-description'>This property will no longer show on this profile.</div>
      </div>
    </ConfirmEl>
  );

  const PropertyChangeLog = () => {
    return (
      <ModalEl
        sel='.property-change-log-modal'
        attrs={{
          open: true,
          closeable: true,
        }}
        on={{
          change: (ev) => {
            if (ev.detail.state === Visibility.Closed) {
              this.helpers.handleOpenPropertyChangelog(false);
            }
          },
        }}
      >
        <div sel='.mp-modal-content'>
          <div sel='.property-change-log-title'>
            <h2>
              {getFormattedPropertyName(property)} [{getScdEventValue(property)}]
            </h2>
            <h2>Property changelog</h2>
          </div>
          <div sel='.property-change-log-description'>
            Historical properties store changes over time, unlike standard properties which only retain the current
            value.
          </div>
          <HistoricalPropertyChangeLogEl dataGroupId={dataGroupId} distinctId={distinctId} property={property} />
          <div sel='.property-change-log-done-btn'>
            <ButtonEl
              attrs={{
                theme: ButtonTheme.Primary,
              }}
              on={{click: () => this.helpers.handleOpenPropertyChangelog(false)}}
            >
              {t(`common.done`)}
            </ButtonEl>
          </div>
        </div>
      </ModalEl>
    );
  };

  return (
    <div
      sel='.formatted-property'
      class={{
        'edit-disabled': propertyEditDisabled || !hasWriteProfilePermission,
        /* TODO - for scd prop, cursor should be inherit... */
      }}
      on={{
        click:
          isEditing || propertyEditDisabled || !hasWriteProfilePermission || sidebarMode || isScdProp
            ? null
            : this.helpers.handleEditProperty,
      }}
    >
      {isEditing ? <EditingProperty /> : <ViewProperty />}
      {isConfirmingDelete && <ConfirmDelete />}
      {isPropertyChangeLogOpen && <PropertyChangeLog />}
    </div>
  );
}
